import { useState } from 'react';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import ManageColumnsPopover from './ManageColumnsPopover';
import type { ColumnVisibilityControl } from './types';

interface ColumnActionsMenuProps {
  column: string;
  visibility: ColumnVisibilityControl;
}

type OpenPopup = { type: 'actions' | 'manage'; anchorEl: HTMLElement } | null;

const ColumnActionsMenu = ({ column, visibility }: ColumnActionsMenuProps) => {
  const [popup, setPopup] = useState<OpenPopup>(null);
  const { columns, hiddenColumnIds, onChange } = visibility;
  const canHide = columns.filter(item => !hiddenColumnIds.includes(item.id)).length > 1;
  const closePopup = () => setPopup(null);

  const hideColumn = () => {
    if (canHide && !hiddenColumnIds.includes(column)) onChange([...hiddenColumnIds, column]);
    closePopup();
  };

  return (
    <>
      <IconButton
        id={`${column}-button`}
        size="small"
        type="button"
        data-column-menu-trigger
        aria-label={`Actions for column ${column}`}
        aria-haspopup="menu"
        aria-controls={popup?.type === 'actions' ? `${column}-menu` : undefined}
        aria-expanded={popup?.type === 'actions'}
        onClick={event => {
          event.stopPropagation();
          setPopup({ type: 'actions', anchorEl: event.currentTarget });
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      {popup?.type === 'actions' && (
        <Menu
          anchorEl={popup.anchorEl}
          open
          onClose={closePopup}
          onClick={event => event.stopPropagation()}
          slotProps={{ list: { id: `${column}-menu`, 'aria-labelledby': `${column}-button` } }}
        >
          <MenuItem disabled={!canHide} onClick={hideColumn}>
            <ListItemIcon>
              <VisibilityOffOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Hide column</ListItemText>
          </MenuItem>
          <MenuItem aria-haspopup="dialog" onClick={() => setPopup({ type: 'manage', anchorEl: popup.anchorEl })}>
            <ListItemIcon>
              <ViewColumnOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manage columns</ListItemText>
          </MenuItem>
        </Menu>
      )}
      {popup?.type === 'manage' && (
        <ManageColumnsPopover anchorEl={popup.anchorEl} visibility={visibility} onClose={closePopup} />
      )}
    </>
  );
};

export default ColumnActionsMenu;
